import { MarkerPin01, Tag01, Users01 } from '@untitledui/icons';

import { CrmObjectAvatar } from '@/lib/crm/components/CrmObjectAvatar';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

export interface EntityHoverCompanyPreviewProps {
  /** Entity must have entity_type_name === 'ahoy_company'. Caller is responsible for the guard. */
  object: CrmObject;
  /**
   * Org context. Currently unused — kept on the props type so the
   * dispatch signature stays uniform with the other body components.
   */
  orgId: number;
}

/**
 * Rich hover preview for `ahoy_company` entities — shows avatar + name +
 * domain in a header row, followed by a property section (industry,
 * headcount range, location). No action footer — companies aren't email
 * recipients, and "Open record" is redundant with the badge click that
 * already opens the slideout.
 *
 * Field selection mirrors the canonical company `detailFieldOrder` from
 * the entity registry (`registry/entityTypeRegistry.ts`), picking the
 * three highest-signal fields for an at-a-glance preview: industry,
 * headcount range, location (city + region).
 */
export function EntityHoverCompanyPreview({
  object,
}: EntityHoverCompanyPreviewProps): React.ReactElement {
  const displayName = getDisplayName(object);
  const domain = getPropertyValue<string>(object.properties?.ahoy_domain);
  const industry = getPropertyValue<string>(object.properties?.ahoy_industry);
  const headcount = getPropertyValue<string>(
    object.properties?.ahoy_headcount_range
  );
  const city = getPropertyValue<string>(object.properties?.ahoy_city);
  const region = getPropertyValue<string>(object.properties?.ahoy_region);
  const location = [city, region].filter(Boolean).join(', ');

  const hasProperties =
    Boolean(industry) || Boolean(headcount) || Boolean(location);

  return (
    <div className='flex flex-col'>
      {/* Header — avatar + name + domain */}
      <div className='flex items-center gap-2.5 p-3'>
        <CrmObjectAvatar object={object} size='xxs' />
        <div className='flex flex-col min-w-0 flex-1 gap-0.5'>
          <span className='text-[13px] font-semibold leading-tight truncate text-[var(--color-text-primary)]'>
            {displayName}
          </span>
          {domain && (
            <span className='text-xs truncate text-[var(--color-text-secondary)]'>
              {domain}
            </span>
          )}
        </div>
      </div>

      {/* Properties — industry, size, location */}
      {hasProperties && (
        <div className='flex flex-col gap-1.5 px-3 pb-3 pt-2.5 border-t border-[var(--color-border-secondary)]'>
          {industry && (
            <PropertyRow
              icon={
                <Tag01 className='size-3.5 text-[var(--color-fg-quaternary)]' />
              }
              label='Industry'
              value={industry}
            />
          )}
          {headcount && (
            <PropertyRow
              icon={
                <Users01 className='size-3.5 text-[var(--color-fg-quaternary)]' />
              }
              label='Size'
              value={headcount}
            />
          )}
          {location && (
            <PropertyRow
              icon={
                <MarkerPin01 className='size-3.5 text-[var(--color-fg-quaternary)]' />
              }
              label='Location'
              value={location}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface PropertyRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function PropertyRow({
  icon,
  label,
  value,
}: PropertyRowProps): React.ReactElement {
  return (
    <div className='flex items-center gap-2 min-w-0'>
      <div className='flex items-center gap-1.5 w-20 shrink-0 text-[var(--color-text-secondary)]'>
        {icon}
        <span className='text-[11px] font-medium'>{label}</span>
      </div>
      <span className='text-[13px] font-medium text-[var(--color-text-primary)] truncate min-w-0 flex-1'>
        {value}
      </span>
    </div>
  );
}
